<template>
  <AppPopover
    v-if="formattedTimes.length > 0"
    ref="AppPopover"
    placement="top"
    trigger="hover"
    popover-container-classes="daily-report-table-popover-trigger"
    extra-popover-classes="daily-report-table-popover-container"
    lazy
    :delay="0"
  >
    <template #body>
      <div
        v-for="row in formattedTimes"
        :key="row.id"
        class="d-flex justify-content-between gap-15 daily-report-table-popover-row"
      >
        <div class="d-flex align-items-center gap-5">
          <div
            class="color-box"
            :style="{ background: row.color }"
          />
          <div class="name">{{ row.color_label }}</div>
        </div>

        <div class="time">{{ row.formatted_time }}</div>
      </div>
    </template>
    <template #trigger>
      <i class="hi hi-time hi-12" />
    </template>
  </AppPopover>
</template>

<script>
import { castArray, groupBy, sumBy } from 'lodash-es';
import AppPopover from '~/components/AppPopover.vue';
import { formatDuration } from '~/utils/time_utils';
import { CHART_DATA_COLORS } from '~/app_constants';

export default {
  name: 'DailyReportTablePopover',
  components: { AppPopover },
  props: {
    timeOffTimes: { type: [Array, Object], default: () => [] },
    overtimes: { type: [Array, Object], default: () => [] },
    showFinancial: { type: Boolean, default: false },
    currency: { type: Object, required: true },
  },
  computed: {
    CHART_DATA_COLORS,
    groupedTimeOffTimes() {
      if (!this.showFinancial) {
        // Combine all time off as user cannot see the rates
        return { 0: castArray(this.timeOffTimes) };
      }

      return groupBy(castArray(this.timeOffTimes), 'rate');
    },
    groupedOvertimes() {
      // When financial, group by label+rate so entries with different pay rates remain
      // separate rows (e.g. totals column when the member's rate changed mid-period).
      // When non-financial, group by label only — this surfaces the daily/weekly split
      // in the flexible-overtime path without exposing rate information.
      if (this.showFinancial) {
        return groupBy(castArray(this.overtimes), (e) => `${e.color_label}||${e.rate}`);
      }

      return groupBy(castArray(this.overtimes), 'color_label');
    },
    allTimesSumByRates() {
      return [this.groupedTimeOffTimes, this.groupedOvertimes].flatMap((groupedTimes) =>
        Object.values(groupedTimes)
          .filter((times) => times.length > 0)
          .map((times) => ({ rate: times[0].rate, color_label: times[0].color_label, time: sumBy(times, 'time') })),
      );
    },
    formattedTimes() {
      return this.allTimesSumByRates.map((row) => {
        const formattedTime = formatDuration(row.time, { showSeconds: 'never' });

        return {
          ...row,
          id: `${row.color_label}-${row.rate}`,
          color: this.CHART_DATA_COLORS[row.color_label] ?? this.CHART_DATA_COLORS.Overtime,
          formatted_time: this.showFinancial
            ? `${formattedTime} at ${row.rate.formatMoney({ ...this.currency, strip_insignificant_zeros: true })}/hr`
            : formattedTime,
        };
      });
    },
  },
};
</script>
